import React from "react";
import ReactDOM from "react-dom/client";
// @react-pdf/renderer relies on Node's global `Buffer` for its internal
// pdfkit dependency. CRA 5 no longer polyfills Node built-ins, so the PDF
// preview blew up silently with "Buffer is not defined" and Chromium ended
// up showing a broken-document icon in the iframe. Wiring the polyfill on
// window BEFORE React mounts fixes the photobook PDF preview.
import { Buffer as _NodeBuffer } from "buffer";
if (typeof window !== "undefined" && !window.Buffer) {
  window.Buffer = _NodeBuffer;
}
import "@/index.css";
import "@/i18n";
import App from "@/App";

// PERF (jun 2026): Sentry loads as an async chunk AND only once the browser
// is idle (or after `load` + 2 s fallback). Loading it immediately made its
// ~150 KB chunk compete with main.js and the hero image for mobile bandwidth,
// adding seconds to the landing LCP. Errors thrown before Sentry attaches are
// queued through the `sentryReady` promise, so React-caught errors still land.
const sentryReady = new Promise((resolve) => {
  const load = () => import("@/instrument").then(resolve).catch(() => resolve(null));
  if ("requestIdleCallback" in window) {
    requestIdleCallback(load, { timeout: 5000 });
  } else if (document.readyState === "complete") {
    setTimeout(load, 2000);
  } else {
    window.addEventListener("load", () => setTimeout(load, 2000));
  }
});

const reportToSentry = (error, errorInfo) => {
  sentryReady
    .then((mod) => {
      const Sentry = mod?.default;
      if (!Sentry?.captureException) return;
      const componentStack = errorInfo?.componentStack;
      Sentry.captureException(
        error,
        componentStack
          ? { captureContext: { contexts: { react: { componentStack } } } }
          : undefined,
      );
    })
    .catch(() => {});
};

const container = document.getElementById("root");

const root = ReactDOM.createRoot(container, {
  // Wire React 19 error hooks into Sentry so all errors (uncaught, caught,
  // recoverable) are reported with React component stacks.
  onUncaughtError: (error, errorInfo) => {
    // eslint-disable-next-line no-console
    console.warn("Uncaught React error:", error, errorInfo?.componentStack);
    reportToSentry(error, errorInfo);
  },
  onCaughtError: (error, errorInfo) => reportToSentry(error, errorInfo),
  onRecoverableError: (error, errorInfo) => reportToSentry(error, errorInfo),
});

root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);

// Quita la pantalla de arranque (logo sobre blanco) en cuanto React ha pintado.
(() => {
  const boot = document.getElementById('tovias-boot');
  if (!boot || boot.style.display === 'none') return;
  const hide = () => {
    boot.style.opacity = '0';
    setTimeout(() => boot.remove(), 400);
  };
  // Dos frames para asegurar que el primer render ya está en pantalla; tope 4 s.
  requestAnimationFrame(() => requestAnimationFrame(() => setTimeout(hide, 250)));
  setTimeout(hide, 4000);
})();

// Register service worker for PWA and offline support.
// Iter 75: bypass the browser's HTTP cache on the SW script itself
// (`updateViaCache: 'none'`) AND force an update check on every page load.
// Without this, browsers happily cache the SW file for up to 24h, which
// is why iter-70+ fixes never propagated to users in production. We also
// listen for `controllerchange` and reload the page once so the new SW
// takes effect immediately on the active tab.
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/service-worker.js', { updateViaCache: 'none' })
      .then((registration) => {
        console.log('Service Worker registered:', registration.scope);
        // Proactively check for an updated SW on every load.
        try { registration.update(); } catch { /* noop */ }
      })
      .catch((error) => {
        console.log('Service Worker registration failed:', error);
      });

    // When a NEW SW takes over from a previous one, reload once so the user
    // gets the new behaviour without closing/reopening the PWA.
    // CRITICAL (jun 2026): skip the reload on the FIRST install. The SW calls
    // skipWaiting()+clients.claim(), so on a cold visit `controllerchange`
    // fired seconds after first paint and reloaded the whole page mid-load.
    // Every first-time visitor (and every PageSpeed run) paid a double page
    // load — this alone accounted for ~7 s of mobile LCP.
    let hadController = !!navigator.serviceWorker.controller;
    let reloading = false;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (!hadController) { hadController = true; return; }
      if (reloading) return;
      reloading = true;
      window.location.reload();
    });
  });
}
